import { AtSign, X } from 'lucide-react';
import { Fragment } from 'react';
import { NavLink } from 'react-router-dom';

import { cn } from '../lib/cn.js';
import { useVisibleNavItems } from '../lib/nav.js';

import { FolderNav } from './folders/FolderNav.js';
import { StorageMeter } from './StorageMeter.js';
import { UserCard } from './UserCard.js';

export function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const items = useVisibleNavItems();

  return (
    <>
      {/* 移动端遮罩。md 以上侧栏常驻，不需要遮罩。 */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-slate-950/40 backdrop-blur-sm transition-opacity md:hidden',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        className={cn(
          // 移动端：固定定位的抽屉，靠 translate 滑入滑出。
          'fixed inset-y-0 left-0 z-50 flex w-60 shrink-0 flex-col border-r border-slate-200 bg-white transition-transform duration-200 dark:border-slate-800 dark:bg-slate-950',
          // md 以上：回到普通 flex 子项，永远可见。
          'md:static md:z-auto md:translate-x-0',
          open ? 'translate-x-0' : 'pointer-events-none -translate-x-full md:pointer-events-auto',
        )}
      >
        {/* h-16 + border-b：与 Topbar 同高同底线，品牌区和顶栏的分隔线连成一条。 */}
        <div className="flex h-16 shrink-0 items-center gap-2.5 border-b border-slate-200 px-5 dark:border-slate-800">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-500 text-white">
            <AtSign className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">AliasHub</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">
              Email Alias &amp; Inbox
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭菜单"
            className="touch-target -mr-1 inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-900 md:hidden"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-3">
          {items.map((item) => (
            <Fragment key={item.key}>
              <NavLink
                to={item.to}
                end={item.to === '/app'}
                className={({ isActive }) =>
                  cn(
                    // min-h-[44px]：触屏最小点击目标，避免误触相邻菜单项。
                    'group flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors whitespace-nowrap',
                    isActive
                      ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300'
                      : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-900',
                  )
                }
              >
                <item.icon className="h-4 w-4 shrink-0" />
                <span className="flex-1">{item.label}</span>
                {item.badge ? (
                  <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                    {item.badge}
                  </span>
                ) : null}
              </NavLink>
              {/* 参考 QQ 邮箱：「我的文件夹」挂在收件箱下面；隐藏收件箱菜单时一并隐藏 */}
              {item.key === 'inbox' ? <FolderNav /> : null}
            </Fragment>
          ))}
        </nav>

        <div className="border-t border-slate-200 px-4 py-3 dark:border-slate-800">
          <StorageMeter />
        </div>
        <div className="pb-safe border-t border-slate-200 px-3 py-3 dark:border-slate-800">
          <UserCard />
        </div>
      </aside>
    </>
  );
}
